/**
 * @file timesheetService.js
 * @description Supabase persistence layer for the Timesheet Tracker. Replaces
 *   the prototype's JSON localStorage with per-record CRUD against the tt_*
 *   tables (all RLS-scoped to auth.uid()). Also holds the per-user service
 *   seeding (from authorizations.js), the consent read/write, the optional
 *   read-only agency typeahead (reads agency_profiles), and north-star
 *   instrumentation via the existing eventService.
 *
 *   All write helpers stamp user_id explicitly; RLS enforces ownership too.
 *
 * @module lib/timesheetService
 */
import { supabase } from "./supabase";
import { trackEvent } from "./eventService";
import { track } from "./analytics";
import { computeIncome, computeUnits } from "./timesheetTime";
import { normalizeName } from "./timesheetImport";
import { recurringExpenseDates } from "./timesheetCalc";

/**
 * Privacy notice version stored with the consent record.
 *   tracker-1.0  full-screen consent page, accepted before first use.
 *   tracker-2.0  inline notice (privacy line + "How your data is handled" sheet), no blocking page.
 *                Consent is recorded automatically on the provider's first successful save
 *                (time entry, expense or completed import) by ensureConsentRecorded below.
 * Providers who accepted 1.0 stay consented (see hasConsented), so they see no change.
 */
export const CONSENT_VERSION = "tracker-2.0";
const LEGACY_CONSENT_VERSIONS = ["tracker-1.0"];

// ─── Load everything for the current user (one parallel fan-out) ────────────
/**
 * Load all tracker data for a user. Returns plain arrays + settings, shaped for
 * the pure calc engine (snake_case Supabase rows).
 */
export async function loadTrackerData(userId) {
  if (!userId) {
    return {
      settings: null,
      agencies: [],
      clients: [],
      services: [],
      timeEntries: [],
      expenses: [],
      paymentSchedules: [],
      paymentRecords: [],
    };
  }
  const [settings, agencies, clients, services, timeEntries, expenses, paymentSchedules, paymentRecords] =
    await Promise.all([
      supabase.from("tt_settings").select("*").eq("user_id", userId).maybeSingle(),
      supabase.from("tt_agencies").select("*").eq("user_id", userId).order("created_at"),
      supabase.from("tt_clients").select("*").eq("user_id", userId).order("created_at"),
      supabase.from("tt_services").select("*").eq("user_id", userId).order("created_at"),
      supabase.from("tt_time_entries").select("*").eq("user_id", userId).order("date", { ascending: false }),
      supabase.from("tt_expenses").select("*").eq("user_id", userId).order("date", { ascending: false }),
      supabase.from("tt_payment_schedules").select("*").eq("user_id", userId),
      supabase.from("tt_payment_records").select("*").eq("user_id", userId).order("received_date"),
    ]);

  const firstError =
    settings.error || agencies.error || clients.error || services.error ||
    timeEntries.error || expenses.error || paymentSchedules.error || paymentRecords.error;
  if (firstError) throw firstError;

  return {
    settings: noteConsent(userId, settings.data || null),
    agencies: agencies.data || [],
    clients: clients.data || [],
    services: services.data || [],
    timeEntries: timeEntries.data || [],
    expenses: expenses.data || [],
    paymentSchedules: paymentSchedules.data || [],
    paymentRecords: paymentRecords.data || [],
  };
}

// ─── Settings + consent ─────────────────────────────────────────────────────
export async function loadSettings(userId) {
  if (!userId) return null;
  const { data, error } = await supabase
    .from("tt_settings").select("*").eq("user_id", userId).maybeSingle();
  if (error) throw error;
  return data || null;
}

/** True once the user has a consent on file: the current notice version or an earlier one they accepted. */
export function hasConsented(settings) {
  return (
    !!settings && !!settings.consent_at &&
    (settings.consent_version === CONSENT_VERSION || LEGACY_CONSENT_VERSIONS.includes(settings.consent_version))
  );
}

/**
 * Record consent. Upserts a tt_settings row with the version + timestamp and the
 * chosen share opt-in (the future nudge bridge; default OFF).
 */
export async function recordConsent(userId, { shareOptIn = false } = {}) {
  if (!userId) throw new Error("recordConsent: missing userId");
  const { data, error } = await supabase
    .from("tt_settings")
    .upsert(
      {
        user_id: userId,
        consent_version: CONSENT_VERSION,
        consent_at: new Date().toISOString(),
        share_opt_in: !!shareOptIn,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id" }
    )
    .select()
    .single();
  if (error) throw error;
  trackEvent(userId, "tracker_consent", { version: CONSENT_VERSION, share_opt_in: !!shareOptIn });
  return data;
}

// Users known (this session) to have a consent on file, and saves in flight. Keeps the write path to one
// settings read per session at most.
const consentOnFile = new Set();
const consentInFlight = new Map();

/** Remember a loaded settings row: a consented user needs no more consent checks. Returns the row. */
function noteConsent(userId, settings) {
  if (userId && hasConsented(settings)) consentOnFile.add(userId);
  return settings;
}

/**
 * The ONE place consent gets recorded (tracker-2.0, inline notice). Called after every first-class save
 * (createTimeEntry, createExpense, executeImport, executeExpenseImport). When the provider has no consent on
 * file it calls recordConsent with their current "Improve my case recommendations" choice (off unless they
 * turned it on in settings). Never throws: a consent hiccup must not fail the provider's save.
 */
export async function ensureConsentRecorded(userId) {
  if (!userId || consentOnFile.has(userId)) return;
  if (consentInFlight.has(userId)) return consentInFlight.get(userId);
  const run = (async () => {
    try {
      const current = await loadSettings(userId);
      if (!hasConsented(current)) await recordConsent(userId, { shareOptIn: current?.share_opt_in === true });
      consentOnFile.add(userId);
    } catch (e) {
      console.error("[Tracker] consent record failed", e);
    } finally {
      consentInFlight.delete(userId);
    }
  })();
  consentInFlight.set(userId, run);
  return run;
}

export async function updateSettings(userId, patch) {
  if (!userId) throw new Error("updateSettings: missing userId");
  const { data, error } = await supabase
    .from("tt_settings")
    .upsert({ user_id: userId, ...patch, updated_at: new Date().toISOString() }, { onConflict: "user_id" })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ─── Service catalog (role-grouped, selectable) ─────────────────────────────
/**
 * The full suggestable service catalog, grouped by credential tier. A user
 * activates the ones they bill for (an active service = a tt_services row whose
 * name matches a catalog item). CPT codes align with authorizations.js so
 * coverage alerts and the rest of the app stay consistent — Behavior Treatment
 * (97155) and Parent Training (97156) keep their canonical names.
 *
 * Names are stored canonically (English) for data integrity + alias matching;
 * the UI localizes group headers and chrome, not the clinical service names.
 */
export const SERVICE_CATALOG = [
  {
    groupKey: "directCare",
    services: [
      { name: "Direct Therapy",        cpt_code: "97153", icon: "⭐", billable_default: true },
      { name: "Group Therapy",         cpt_code: "97154", icon: "👥", billable_default: true },
      { name: "Supporting Assessment", cpt_code: "97152", icon: "📝", billable_default: true },
      { name: "Cancellation",          cpt_code: null,    icon: "🚫", billable_default: false },
    ],
  },
  {
    groupKey: "behaviorTreatment",
    services: [
      { name: "Behavior Treatment", cpt_code: "97155", icon: "🧩", billable_default: true },
    ],
  },
  {
    groupKey: "bcba",
    services: [
      { name: "Initial Assessment",    cpt_code: "97151", icon: "📋", billable_default: true },
      { name: "Parent Training",       cpt_code: "97156", icon: "👪", billable_default: true },
      { name: "Group Parent Training", cpt_code: "97157", icon: "👨‍👩‍👧", billable_default: true },
      { name: "Re-assessment",         cpt_code: null,    icon: "🔄", billable_default: true },
    ],
  },
  {
    groupKey: "common",
    services: [
      { name: "Supervision",   cpt_code: null, icon: "🧑‍🏫", billable_default: true },
      { name: "Documentation", cpt_code: null, icon: "🗒️", billable_default: false },
      { name: "Travel",        cpt_code: null, icon: "🚗", billable_default: false },
    ],
  },
];

// Catalog group keys suggested per role (BT/RBT live under the "rbt" role).
export const SUGGESTED_GROUPS_BY_ROLE = {
  rbt:   ["directCare", "common"],
  bcaba: ["behaviorTreatment", "directCare", "common"],
  bcba:  ["bcba", "behaviorTreatment", "common"],
};

// Minimal set activated on first run so logging works immediately (<2-min aha).
export const SEED_SERVICE_NAMES_BY_ROLE = {
  rbt:   ["Direct Therapy", "Supervision"],
  bcaba: ["Behavior Treatment", "Direct Therapy", "Supervision"],
  bcba:  ["Behavior Treatment", "Parent Training", "Initial Assessment", "Supervision"],
};

/** Flat list of every catalog service (handy for name → item lookups). */
export function allCatalogServices() {
  return SERVICE_CATALOG.flatMap((g) => g.services);
}

/**
 * Seed the role-appropriate default services for a user if they have none yet,
 * so they can log time without first configuring the catalog. Returns rows.
 */
export async function seedServicesIfEmpty(userId, role = "rbt") {
  if (!userId) return [];
  const { data: existing, error: readErr } = await supabase
    .from("tt_services").select("id").eq("user_id", userId).limit(1);
  if (readErr) throw readErr;
  if (existing && existing.length > 0) return existing;

  const names = SEED_SERVICE_NAMES_BY_ROLE[role] || SEED_SERVICE_NAMES_BY_ROLE.rbt;
  const all = allCatalogServices();
  const rows = names
    .map((n) => all.find((s) => s.name === n))
    .filter(Boolean)
    .map((s) => ({ ...s, user_id: userId }));
  if (!rows.length) return [];
  const { data, error } = await supabase.from("tt_services").insert(rows).select();
  if (error) throw error;
  return data || [];
}

// ─── Agencies ───────────────────────────────────────────────────────────────
/** Default bi-weekly payment template: 14-day cycle, paid 15 days after end. */
export const PAYMENT_TEMPLATES = {
  semiMonthlyFriday: {
    label: "Semi-monthly (1–15 / 16–end), paid every other Friday",
    pay_period_type: "semi_monthly",
    cycle_length_days: null,
    pay_delay_days: 0,
    pay_weekday: null,
    pay_date_rule: "anchored_cadence",
    pay_anchor_date: null,
    pay_cadence_days: 14,
  },
  biweekly15: { label: "Bi-weekly, paid 15 days after period end", pay_period_type: "fixed_days", cycle_length_days: 14, pay_delay_days: 15, pay_weekday: null, pay_date_rule: "delay_weekday" },
  biweekly7: { label: "Bi-weekly, paid 7 days after period end", pay_period_type: "fixed_days", cycle_length_days: 14, pay_delay_days: 7, pay_weekday: null, pay_date_rule: "delay_weekday" },
  weekly7: { label: "Weekly, paid 7 days after period end", pay_period_type: "fixed_days", cycle_length_days: 7, pay_delay_days: 7, pay_weekday: null, pay_date_rule: "delay_weekday" },
  monthly15: { label: "Monthly, paid 15 days after period end", pay_period_type: "fixed_days", cycle_length_days: 30, pay_delay_days: 15, pay_weekday: null, pay_date_rule: "delay_weekday" },
};

export async function createAgency(userId, values) {
  if (!userId) throw new Error("createAgency: missing userId");
  const row = {
    user_id: userId,
    name: (values.name || "").trim(),
    platform_agency_id: values.platform_agency_id || null,
    notes: values.notes || null,
    service_rates: values.service_rates || {},
    payment_enabled: !!values.payment_enabled,
    anchor_period_start: values.anchor_period_start || null,
    cycle_length_days: values.cycle_length_days != null ? Number(values.cycle_length_days) : null,
    pay_delay_days: values.pay_delay_days != null ? Number(values.pay_delay_days) : null,
    pay_period_type: values.pay_period_type || "fixed_days",
    pay_weekday: values.pay_weekday == null ? null : Number(values.pay_weekday),
    pay_date_rule: values.pay_date_rule || "delay_weekday",
    pay_anchor_date: values.pay_anchor_date || null,
    pay_cadence_days: values.pay_cadence_days == null ? null : Number(values.pay_cadence_days),
    pay_on_business_days: values.pay_on_business_days !== false,
    reportable_1099: values.reportable_1099 !== false,
  };
  const { data, error } = await supabase.from("tt_agencies").insert(row).select().single();
  if (error) throw error;
  return data;
}

export async function updateAgency(userId, id, patch) {
  const { data, error } = await supabase
    .from("tt_agencies")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id).eq("user_id", userId)
    .select().single();
  if (error) throw error;
  return data;
}

export async function deleteAgency(userId, id) {
  const { error } = await supabase.from("tt_agencies").delete().eq("id", id).eq("user_id", userId);
  if (error) throw error;
}

/**
 * OPTIONAL read-only typeahead over real platform agencies. Reads agency_profiles
 * only — never writes. Filters out suspended agencies. Returns lightweight rows
 * the picker can render; the chosen user_id is stored as platform_agency_id.
 */
export async function searchPlatformAgencies(query, limit = 8) {
  const q = (query || "").trim();
  if (q.length < 2) return [];
  const { data, error } = await supabase
    .from("agency_public_profiles") // public card; suspended agencies are already left out
    .select("user_id, agency_name, city, state, logo_url")
    .ilike("agency_name", `%${q}%`)
    .limit(limit);
  if (error) return []; // typeahead is best-effort; never block manual entry
  return (data || []).map((a) => ({
    platformAgencyId: a.user_id,
    name: a.agency_name,
    city: a.city,
    state: a.state,
    logoUrl: a.logo_url,
  }));
}

// ─── Clients ────────────────────────────────────────────────────────────────
export async function createClient(userId, values) {
  if (!userId) throw new Error("createClient: missing userId");
  const row = {
    user_id: userId,
    agency_id: values.agency_id || null,
    initials: (values.initials || "").trim(),
    color: values.color || null,
    hourly_rate: values.hourly_rate === "" || values.hourly_rate == null ? null : Number(values.hourly_rate),
    service_rates: values.service_rates || {},
    is_discontinued: !!values.is_discontinued,
    discontinued_on: values.is_discontinued ? values.discontinued_on || null : null,
  };
  const { data, error } = await supabase.from("tt_clients").insert(row).select().single();
  if (error) throw error;
  return data;
}

export async function saveExpenseCategories(userId, categories) {
  return updateSettings(userId, { expense_categories: categories });
}

export async function updateClient(userId, id, patch) {
  const { data, error } = await supabase
    .from("tt_clients")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id).eq("user_id", userId)
    .select().single();
  if (error) throw error;
  return data;
}

export async function deleteClient(userId, id) {
  const { error } = await supabase.from("tt_clients").delete().eq("id", id).eq("user_id", userId);
  if (error) throw error;
}

// ─── Services (manual add beyond seeds) ─────────────────────────────────────
export async function createService(userId, values) {
  if (!userId) throw new Error("createService: missing userId");
  const row = {
    user_id: userId,
    name: (values.name || "").trim(),
    icon: values.icon || "📋",
    cpt_code: values.cpt_code || null,
    billable_default: values.billable_default !== false,
  };
  const { data, error } = await supabase.from("tt_services").insert(row).select().single();
  if (error) throw error;
  return data;
}

export async function deleteService(userId, id) {
  const { error } = await supabase.from("tt_services").delete().eq("id", id).eq("user_id", userId);
  if (error) throw error;
}

// ─── Time entries (income + units computed at write; rate snapshotted) ──────
function buildEntryRow(userId, values) {
  const duration = Number(values.duration_minutes) || 0;
  const rate = Number(values.rate_snapshot) || 0;
  return {
    user_id: userId,
    date: values.date,
    agency_id: values.agency_id || null,
    client_id: values.client_id || null,
    service_id: values.service_id || null,
    start_minutes: values.start_minutes != null ? Number(values.start_minutes) : null,
    end_minutes: values.end_minutes != null ? Number(values.end_minutes) : null,
    duration_minutes: duration,
    units: computeUnits(duration),
    rate_snapshot: rate,
    billable: values.billable !== false,
    reportable_1099: values.reportable_1099 !== false,
    income: computeIncome(duration, rate),
    notes: values.notes || null,
  };
}

export async function createTimeEntry(userId, values) {
  if (!userId) throw new Error("createTimeEntry: missing userId");
  const { data, error } = await supabase
    .from("tt_time_entries").insert(buildEntryRow(userId, values)).select().single();
  if (error) throw error;
  await ensureConsentRecorded(userId);
  return data;
}

export async function updateTimeEntry(userId, id, values) {
  // Recompute income/units/rate snapshot on edit so corrections stay correct.
  const row = { ...buildEntryRow(userId, values), updated_at: new Date().toISOString() };
  delete row.user_id; // never reassign owner
  const { data, error } = await supabase
    .from("tt_time_entries").update(row).eq("id", id).eq("user_id", userId).select().single();
  if (error) throw error;
  return data;
}

export async function deleteTimeEntry(userId, id) {
  const { error } = await supabase.from("tt_time_entries").delete().eq("id", id).eq("user_id", userId);
  if (error) throw error;
}

// ─── Expenses ───────────────────────────────────────────────────────────────
export async function createExpense(userId, values) {
  if (!userId) throw new Error("createExpense: missing userId");
  const row = {
    user_id: userId,
    date: values.date,
    agency_id: values.agency_id || null,
    client_id: values.client_id || null,
    category: values.category || null,
    amount: Number(values.amount) || 0,
    notes: values.notes || null,
    is_agency_deducted: values.is_agency_deducted === true,
    is_reimbursable: values.is_reimbursable === true,
  };
  const { data, error } = await supabase.from("tt_expenses").insert(row).select().single();
  if (error) throw error;
  await ensureConsentRecorded(userId);
  return data;
}

export async function updateExpense(userId, id, values) {
  if (!userId) throw new Error("updateExpense: missing userId");
  const row = {
    date: values.date,
    agency_id: values.agency_id || null,
    client_id: values.client_id || null,
    category: values.category || null,
    amount: Number(values.amount) || 0,
    notes: values.notes || null,
    is_agency_deducted: values.is_agency_deducted === true,
    is_reimbursable: values.is_reimbursable === true,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from("tt_expenses").update(row).eq("id", id).eq("user_id", userId).select().single();
  if (error) throw error;
  return data;
}

export async function deleteExpense(userId, id) {
  const { error } = await supabase.from("tt_expenses").delete().eq("id", id).eq("user_id", userId);
  if (error) throw error;
}

/**
 * Create a recurring expense as concrete rows — one per occurrence from
 * values.date through `until`, all sharing a recurrence_group_id so the series
 * can be managed/deleted together. Falls back to a single insert when frequency
 * is "none" or only one occurrence results.
 */
export async function createRecurringExpenses(userId, values, { frequency, until } = {}) {
  if (!userId) throw new Error("createRecurringExpenses: missing userId");
  const dates = recurringExpenseDates(values.date, frequency, until);
  if (!frequency || frequency === "none" || dates.length <= 1) {
    return [await createExpense(userId, values)];
  }
  const groupId = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `rg-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
  const rows = dates.map((date) => ({
    user_id: userId,
    date,
    agency_id: values.agency_id || null,
    client_id: values.client_id || null,
    category: values.category || null,
    amount: Number(values.amount) || 0,
    notes: values.notes || null,
    is_agency_deducted: values.is_agency_deducted === true,
    is_reimbursable: values.is_reimbursable === true,
    recurrence: frequency,
    recurrence_group_id: groupId,
  }));
  const { data, error } = await supabase.from("tt_expenses").insert(rows).select();
  if (error) throw error;
  trackEvent(userId, "tracker_recurring_expense", { frequency, count: rows.length });
  return data;
}

/** Delete every expense in a recurrence series. */
export async function deleteExpenseSeries(userId, groupId) {
  if (!userId || !groupId) throw new Error("deleteExpenseSeries: missing args");
  const { error } = await supabase
    .from("tt_expenses").delete().eq("recurrence_group_id", groupId).eq("user_id", userId);
  if (error) throw error;
}

/**
 * Bulk-edit the field values (amount/category/agency/client/notes) of a recurring
 * series — every row from `fromDate` forward. Dates are NOT touched (each row keeps
 * its own occurrence date). Only keys present in `patch` are written.
 */
export async function updateExpenseSeries(userId, groupId, patch = {}, { fromDate } = {}) {
  if (!userId || !groupId) throw new Error("updateExpenseSeries: missing args");
  const row = { updated_at: new Date().toISOString() };
  if (patch.agency_id !== undefined) row.agency_id = patch.agency_id || null;
  if (patch.client_id !== undefined) row.client_id = patch.client_id || null;
  if (patch.category !== undefined) row.category = patch.category || null;
  if (patch.amount !== undefined) row.amount = Number(patch.amount) || 0;
  if (patch.notes !== undefined) row.notes = patch.notes || null;
  if (patch.is_agency_deducted !== undefined) row.is_agency_deducted = patch.is_agency_deducted === true;
  if (patch.is_reimbursable !== undefined) row.is_reimbursable = patch.is_reimbursable === true;

  let q = supabase.from("tt_expenses").update(row)
    .eq("recurrence_group_id", groupId).eq("user_id", userId);
  if (fromDate) q = q.gte("date", fromDate);
  const { data, error } = await q.select();
  if (error) throw error;
  return data;
}

/**
 * Extend or shorten a recurring series to end on `newUntil`:
 *   • shorten — delete occurrences dated after newUntil
 *   • extend  — insert the missing occurrence dates (at the series cadence) up to
 *               newUntil, using `template` for the field values
 * Idempotent: regenerates only the dates that don't already exist.
 */
export async function rescheduleExpenseSeries(userId, groupId, { frequency, seriesStart, newUntil, template = {} } = {}) {
  if (!userId || !groupId || !seriesStart || !newUntil) throw new Error("rescheduleExpenseSeries: missing args");

  // Shorten: drop anything past the new end.
  const del = await supabase.from("tt_expenses").delete()
    .eq("recurrence_group_id", groupId).eq("user_id", userId).gt("date", newUntil);
  if (del.error) throw del.error;

  // Extend: insert occurrence dates that should exist but don't.
  const { data: existing, error: exErr } = await supabase.from("tt_expenses")
    .select("date").eq("recurrence_group_id", groupId).eq("user_id", userId);
  if (exErr) throw exErr;
  const have = new Set((existing || []).map((r) => r.date));
  const wanted = recurringExpenseDates(seriesStart, frequency, newUntil);
  const toAdd = wanted.filter((d) => !have.has(d));
  if (toAdd.length) {
    const rows = toAdd.map((date) => ({
      user_id: userId,
      date,
      agency_id: template.agency_id || null,
      client_id: template.client_id || null,
      category: template.category || null,
      amount: Number(template.amount) || 0,
      notes: template.notes || null,
      is_agency_deducted: template.is_agency_deducted === true,
      is_reimbursable: template.is_reimbursable === true,
      recurrence: frequency,
      recurrence_group_id: groupId,
    }));
    const { error: insErr } = await supabase.from("tt_expenses").insert(rows);
    if (insErr) throw insErr;
  }
  return { added: toAdd.length };
}

// ─── Payment schedule reconciliation (upsert override per cycle) ────────────
/**
 * Save the user's reconciliation for a specific cycle. Cycles are derived, so
 * we upsert keyed on (agency_id, period_start, period_end) by first finding an
 * existing row, then update/insert accordingly.
 */
export async function reconcileCycle(userId, cycle, patch) {
  if (!userId) throw new Error("reconcileCycle: missing userId");
  const now = new Date().toISOString();
  // Stamp confirmed_at when actual_received is being set; clear it when unset.
  const confirmedPatch =
    "actual_received" in patch
      ? { confirmed_at: patch.actual_received != null ? now : null }
      : {};
  const base = {
    user_id: userId,
    agency_id: cycle.agencyId,
    pay_date: cycle.payDate || null,
    period_start: cycle.periodStart,
    period_end: cycle.periodEnd,
    updated_at: now,
    ...confirmedPatch,
    ...patch,
  };
  if (cycle.storageId) {
    const { data, error } = await supabase
      .from("tt_payment_schedules").update(base).eq("id", cycle.storageId).eq("user_id", userId)
      .select().single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase
    .from("tt_payment_schedules").insert(base).select().single();
  if (error) throw error;
  return data;
}

/**
 * Hard-delete a payment schedule row. Used for both cycle overrides and
 * manual payment entries. RLS enforces ownership.
 */
export async function deletePaymentSchedule(userId, scheduleId) {
  if (!userId || !scheduleId) return;
  const { error } = await supabase
    .from("tt_payment_schedules")
    .delete()
    .eq("id", scheduleId)
    .eq("user_id", userId);
  if (error) throw error;
}

// ─── Per-cycle payment ledger (installments) ────────────────────────────────
/**
 * Recompute a cycle-override row's actual_received as the SUM of its ledger
 * records, and its confirmed_at (null when the ledger is empty). This keeps the
 * single authoritative number the reconciliation engine reads in sync with the
 * detailed installment breakdown, so no calc logic has to change.
 */
async function recomputeScheduleTotal(userId, scheduleId) {
  const { data: recs, error } = await supabase
    .from("tt_payment_records")
    .select("amount")
    .eq("schedule_id", scheduleId)
    .eq("user_id", userId);
  if (error) throw error;
  const rows = recs || [];
  const now = new Date().toISOString();
  const patch = rows.length
    ? {
        actual_received: Math.round(rows.reduce((s, r) => s + (Number(r.amount) || 0), 0) * 100) / 100,
        confirmed_at: now,
        updated_at: now,
      }
    : { actual_received: null, confirmed_at: null, updated_at: now };
  const { error: upErr } = await supabase
    .from("tt_payment_schedules").update(patch).eq("id", scheduleId).eq("user_id", userId);
  if (upErr) throw upErr;
}

/**
 * Record one payment installment against a cycle. Cycles are derived, so we
 * first ensure the cycle has an override row to anchor records to (creating an
 * empty one when needed), insert the record, then re-sum the parent total.
 *
 * @param {string} userId
 * @param {Object} cycle - the derived cycle (needs storageId/agencyId/period/payDate)
 * @param {{amount:number, received_date:string, note?:string}} payload
 * @returns {Promise<string>} the schedule_id the record was anchored to
 */
export async function addPaymentRecord(userId, cycle, { amount, received_date, note } = {}) {
  if (!userId) throw new Error("addPaymentRecord: missing userId");
  const amt = Number(amount);
  if (isNaN(amt)) throw new Error("addPaymentRecord: invalid amount");

  let scheduleId = cycle.storageId;
  if (!scheduleId) {
    const row = await reconcileCycle(userId, cycle, {}); // create the anchor override row
    scheduleId = row.id;
  }

  const now = new Date().toISOString();
  const { error } = await supabase.from("tt_payment_records").insert({
    user_id: userId,
    schedule_id: scheduleId,
    amount: amt,
    received_date: received_date || now.slice(0, 10),
    note: note || null,
    created_at: now,
  });
  if (error) throw error;

  await recomputeScheduleTotal(userId, scheduleId);
  return scheduleId;
}

/**
 * Delete one payment installment, then re-sum the parent cycle total (which
 * reverts to unpaid when its last record is removed).
 */
export async function deletePaymentRecord(userId, recordId, scheduleId) {
  if (!userId || !recordId) return;
  const { error } = await supabase
    .from("tt_payment_records").delete().eq("id", recordId).eq("user_id", userId);
  if (error) throw error;
  if (scheduleId) await recomputeScheduleTotal(userId, scheduleId);
}

/**
 * Create a manually-entered payment record (is_manual = true). These are not
 * derived from time entries — they capture unexpected income, bonuses,
 * reimbursements, or any out-of-cycle payment. confirmed_at is stamped
 * immediately when actual_received is provided.
 */
export async function createManualPayment(userId, payload) {
  if (!userId) throw new Error("createManualPayment: missing userId");
  const now = new Date().toISOString();
  const hasAmount = payload.actual_received != null && payload.actual_received !== "";
  const { data, error } = await supabase
    .from("tt_payment_schedules")
    .insert({
      user_id: userId,
      agency_id: payload.agency_id || null,
      pay_date: payload.pay_date || null,
      period_start: payload.pay_date || null,
      period_end: payload.pay_date || null,
      actual_received: hasAmount ? Number(payload.actual_received) : null,
      notes: payload.notes || null,
      manual_description: payload.manual_description || null,
      is_manual: true,
      // Catch-up payment for an agency's past-due cycle (cash, not new income) —
      // only meaningful when an agency is selected.
      applies_to_balance: !!(payload.agency_id && payload.applies_to_balance),
      confirmed_at: hasAmount ? now : null,
      created_at: now,
      updated_at: now,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ─── North-star instrumentation ─────────────────────────────────────────────
/**
 * Fire the "aha" event once per user. "aha" = has ≥1 agency AND ≥1 payment
 * cycle. Idempotent within a session via localStorage guard so we don't spam
 * the events table on every load. No-ops gracefully if anything is missing.
 */
export function trackAhaIfReached(userId, reached) {
  if (!userId || !reached) return;
  const key = `tracker:aha:${userId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
  } catch { /* storage blocked — still fire once per load */ }
  // Wedge activation milestone — mirror to GA4 too (no PII) so the tracker→
  // marketplace funnel is visible alongside acquisition source in GA4.
  track("tracker_aha", { userId });
}

// ─── CSV import execution ───────────────────────────────────────────────────
/**
 * Execute an import plan (from timesheetImport.buildImportPlan): auto-create any
 * missing agencies/services/clients, then bulk-insert the OK time entries. All
 * writes go through the user's own session (RLS-scoped). Duplicate/error rows
 * are skipped. Returns counts + the new import_batch_id.
 *
 * @param {string} userId
 * @param {Object} plan - the import plan
 * @param {Object} existing - { agencies, clients, services } currently loaded
 * @param {function} [onProgress] - (done, total) for the insert phase
 */
export async function executeImport(userId, plan, existing = {}, onProgress) {
  if (!userId) throw new Error("executeImport: missing userId");

  // ── 1. Resolve/create agencies (by normalized name) ──
  const agencyByNorm = new Map((existing.agencies || []).map((a) => [normalizeName(a.name), a]));
  for (const name of plan.newAgencies || []) {
    const created = await createAgency(userId, { name });
    agencyByNorm.set(normalizeName(name), created);
  }

  // ── 2. Resolve/create services (by normalized name) ──
  const serviceByNorm = new Map((existing.services || []).map((s) => [normalizeName(s.name), s]));
  for (const name of plan.newServices || []) {
    const created = await createService(userId, { name });
    serviceByNorm.set(normalizeName(name), created);
  }

  // ── 3. Resolve/create clients (agency-scoped, by initials) ──
  const clientKey = (agencyId, initials) => `${agencyId || ""}|${normalizeName(initials)}`;
  const clientByKey = new Map((existing.clients || []).map((c) => [clientKey(c.agency_id, c.initials), c]));
  for (const { initials, agencyName } of plan.newClients || []) {
    const agency = agencyByNorm.get(normalizeName(agencyName));
    const created = await createClient(userId, { agency_id: agency?.id || null, initials });
    clientByKey.set(clientKey(agency?.id, initials), created);
  }

  // ── 4. Build entry rows from the OK plan rows ──
  const okRows = (plan.rows || []).filter((r) => r.status === "ok" && r.resolved);
  const batchId =
    (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : null;
  const rows = okRows.map((r) => {
    const rs = r.resolved;
    const agency = agencyByNorm.get(normalizeName(rs.agencyName));
    const service = serviceByNorm.get(normalizeName(rs.serviceName));
    const client = clientByKey.get(clientKey(agency?.id, rs.clientInitials));
    return {
      user_id: userId,
      date: rs.date,
      agency_id: agency?.id || null,
      client_id: client?.id || null,
      service_id: service?.id || null,
      start_minutes: rs.start_minutes != null ? Number(rs.start_minutes) : null,
      end_minutes: rs.end_minutes != null ? Number(rs.end_minutes) : null,
      duration_minutes: Number(rs.duration_minutes) || 0,
      units: Number(rs.units) || 0,
      rate_snapshot: Number(rs.rate_snapshot) || 0,
      billable: rs.billable !== false,
      reportable_1099: true,
      income: Number(rs.income) || 0,
      notes: rs.notes || null,
      import_batch_id: batchId,
    };
  });

  // ── 5. Bulk insert in chunks (RLS scopes to this user) ──
  let inserted = 0;
  const CHUNK = 100;
  for (let i = 0; i < rows.length; i += CHUNK) {
    const slice = rows.slice(i, i + CHUNK);
    const { error } = await supabase.from("tt_time_entries").insert(slice);
    if (error) throw error;
    inserted += slice.length;
    if (onProgress) onProgress(inserted, rows.length);
  }

  trackEvent(userId, "tracker_import", {
    entries: inserted,
    new_agencies: (plan.newAgencies || []).length,
    new_clients: (plan.newClients || []).length,
    new_services: (plan.newServices || []).length,
  });

  await ensureConsentRecorded(userId);
  return {
    batchId,
    insertedEntries: inserted,
    createdAgencies: (plan.newAgencies || []).length,
    createdServices: (plan.newServices || []).length,
    createdClients: (plan.newClients || []).length,
    skipped: (plan.rows || []).length - inserted,
  };
}

/**
 * Execute an EXPENSE import plan (buildExpenseImportPlan): auto-create any missing
 * agencies, then bulk-insert the OK expense rows tagged with import_batch_id.
 */
export async function executeExpenseImport(userId, plan, existing = {}, onProgress) {
  if (!userId) throw new Error("executeExpenseImport: missing userId");
  const agencyByNorm = new Map((existing.agencies || []).map((a) => [normalizeName(a.name), a]));
  for (const name of plan.newAgencies || []) {
    const created = await createAgency(userId, { name });
    agencyByNorm.set(normalizeName(name), created);
  }
  const okRows = (plan.rows || []).filter((r) => r.status === "ok" && r.resolved);
  const batchId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : null;
  const rows = okRows.map((r) => {
    const rs = r.resolved;
    const agency = rs.agencyName ? agencyByNorm.get(normalizeName(rs.agencyName)) : null;
    return {
      user_id: userId,
      date: rs.date,
      agency_id: agency?.id || null,
      client_id: null,
      category: rs.category || null,
      amount: Number(rs.amount) || 0,
      notes: rs.notes || null,
      import_batch_id: batchId,
    };
  });
  let inserted = 0;
  const CHUNK = 100;
  for (let i = 0; i < rows.length; i += CHUNK) {
    const slice = rows.slice(i, i + CHUNK);
    const { error } = await supabase.from("tt_expenses").insert(slice);
    if (error) throw error;
    inserted += slice.length;
    if (onProgress) onProgress(inserted, rows.length);
  }
  trackEvent(userId, "tracker_import", { kind: "expenses", expenses: inserted, new_agencies: (plan.newAgencies || []).length });
  await ensureConsentRecorded(userId);
  return {
    batchId,
    insertedExpenses: inserted,
    createdAgencies: (plan.newAgencies || []).length,
    createdClients: 0,
    createdServices: 0,
    skipped: (plan.rows || []).length - inserted,
  };
}

/** Undo a CSV import — deletes the entries AND expenses created in a given batch. */
export async function deleteImportBatch(userId, batchId) {
  if (!userId || !batchId) return;
  const e1 = await supabase.from("tt_time_entries").delete().eq("user_id", userId).eq("import_batch_id", batchId);
  if (e1.error) throw e1.error;
  const e2 = await supabase.from("tt_expenses").delete().eq("user_id", userId).eq("import_batch_id", batchId);
  if (e2.error) throw e2.error;
}
